// T-404c — le Bureau « comme un vrai bureau » : une phrase du jour, des groupes par nature, un tri par urgence puis par date.
// Présentation seulement : aucune décision, aucun appel réseau. T-438 : regroupement par nature + tri (urgence, puis date).

import { AlertTriangle, PhoneCall, Send, HelpCircle, Link2, ArrowRightLeft, MoreHorizontal, Archive, ClipboardList, FileText, FolderOpen, Banknote } from "lucide-react";

// T-438 — UNE source pour les natures de cartes : clé, libellé simple, petite icône. Le Bureau (Matthieu, Chloé, Joey) ne connaît pas d'autre liste.
export const NATURES = {
  urgent: { cle: "urgent", titre: "Urgent / Alertes", icone: AlertTriangle },
  rappeler: { cle: "rappeler", titre: "À rappeler", icone: PhoneCall },
  reponses: { cle: "reponses", titre: "Réponses prêtes à envoyer", icone: Send },
  trancher: { cle: "trancher", titre: "Mails à trancher", icone: HelpCircle },
  rattacher: { cle: "rattacher", titre: "À rattacher à un dossier", icone: Link2 },
  transmis: { cle: "transmis", titre: "Transmis par Matthieu", icone: ArrowRightLeft },
  aclasser: { cle: "aclasser", titre: "À classer", icone: Archive },
  autres: { cle: "autres", titre: "Autres", icone: MoreHorizontal },
  completer: { cle: "completer", titre: "À compléter", icone: ClipboardList },
  dal: { cle: "dal", titre: "DAL", icone: FileText },
  depot: { cle: "depot", titre: "Dépôt", icone: FolderOpen },
  avance: { cle: "avance", titre: "Demande d'avance", icone: Banknote },
  solde: { cle: "solde", titre: "Demande de solde", icone: Banknote },
};
const lesNatures = (...cles) => cles.map((k) => NATURES[k]);

// Ordre fixe des groupes. Chloé et Joey suivent l'étape calculée par le code (`carte.etape`), jamais un texte.
export const GROUPES_PAR_AGENT = {
  matthieu: lesNatures("urgent", "rappeler", "reponses", "trancher", "rattacher", "transmis", "aclasser", "autres"),
  chloe: lesNatures("transmis", "completer", "dal", "depot"),
  joey: lesNatures("transmis", "avance", "solde"),
};
export const GROUPES = GROUPES_PAR_AGENT.matthieu;
const GROUPE_DE_ETAPE = { transmission: "transmis", incomplet: "completer", dal_a_faire: "dal", depot_a_faire: "depot", avance_a_faire: "avance", solde_a_faire: "solde" };

const estUrgente = (c) => c?.urgence === "urgent" || c?.urgence === "haute";
const RANG = { urgent: 0, haute: 1 };

// T-445c — « Autres » ne doit rester qu'un filet (≤ 10 % des cartes). Le type TECHNIQUE de la carte (champ `type_technique`, posé par le serveur)
// dit à quelle nature elle appartient quand elle n'a qu'un bouton « Mettre de côté ». Une seule table, ici.
//   question  → l'utilisateur doit répondre ou décider : « Mails à trancher »
//   information → courrier / pièce / décision sans réponse à écrire : « À rattacher » si aucun dossier n'est lié, sinon « À classer »
const TYPES_QUESTION = new Set(["t240_question", "t240_question_client_anah"]);
const TYPES_INFORMATION = new Set(["t240_action_client_anah", "t240_action_prete", "t240_recap_anah", "1_echange", "3_decision", "3a_paiement"]);
function natureSansAction(c) {
  const t = c?.type_technique;
  if (TYPES_QUESTION.has(t) || t === "2_question_instructeur" || t === "6_question_client") return "trancher";
  if (TYPES_INFORMATION.has(t)) return c?.dossier ? "aclasser" : "rattacher";
  if (!t && (c?.source === "ag04" || c?.source === "entrant")) return "trancher";   // « Matthieu n'a pas su classer ce mail » : c'est à l'utilisateur de trancher
  return "autres";
}

/** Nature d'une carte : ce que la personne doit faire. Calculée sur le bouton proposé par le serveur ; sans bouton (cartes « fait » / « de côté »), sur la source. */
export function groupeDe(c, agent = "matthieu") {
  if (agent !== "matthieu") return GROUPE_DE_ETAPE[c?.etape] || GROUPES_PAR_AGENT[agent]?.[0]?.cle;
  const action = c?.bouton?.action;
  const source = c?.source || String(c?.ref || "").split(":")[0];
  if (source === "alerte" || String(c?.ref || "").startsWith("alerte:") || action === "alerte_resolue" || c?.urgence === "urgent") return "urgent";
  if (c?.rappel) return "rappeler";
  if (action === "envoyer" || action === "modifier" || (!action && source === "fiche")) return "reponses";
  if (action === "trancher") return "trancher";
  if (action === "rattacher" || (!action && source === "ag04")) return "rattacher";
  if (source === "transmission") return "transmis";
  return action === "mettre_de_cote" ? natureSansAction(c) : "autres";
}

/** Tri dans un groupe : urgence d'abord, puis date (le plus ancien d'abord). Sans date (files de Chloé et Joey), l'ancienneté en jours remplace la date. */
const dateDe = (c) => {
  const t = c?.date ? Date.parse(c.date) : NaN;
  if (!Number.isNaN(t)) return t;
  return Number.isFinite(c?.anciennete_jours) ? -c.anciennete_jours * 86400000 : Infinity;
};
export function trier(cartes) {
  return cartes.map((c, i) => [c, i]).sort(([a, i], [b, j]) => (RANG[a?.urgence] ?? 2) - (RANG[b?.urgence] ?? 2) || (dateDe(a) - dateDe(b) || 0) || i - j).map(([c]) => c);
}

/** Groupes non vides, dans l'ordre fixe des natures. */
export function grouperCartes(cartes, agent = "matthieu") {
  const liste = Array.isArray(cartes) ? cartes : [];
  return (GROUPES_PAR_AGENT[agent] || GROUPES).map((g) => ({ ...g, cartes: trier(liste.filter((c) => groupeDe(c, agent) === g.cle)) })).filter((g) => g.cartes.length > 0);
}

/** Ordre d'affichage à plat (sert aux raccourcis j / k : la carte « suivante » est celle qu'on voit en dessous). */
export const aPlat = (groupes) => groupes.flatMap((g) => g.cartes);

/** « 7 choses à faire, dont 2 urgentes » — jamais « 0 résultat ». Le « dont N » est le nombre du groupe « Urgent / Alertes » (même fonction `groupeDe`). */
export function phraseDuJour(nbAFaire, cartesAFaire, agent = "matthieu") {
  const n = Number.isFinite(nbAFaire) ? nbAFaire : 0;
  if (n <= 0) return "Rien à faire aujourd'hui. Tout est à jour.";
  const urgente = GROUPES_PAR_AGENT[agent]?.some((g) => g.cle === "urgent") ? (c) => groupeDe(c, agent) === "urgent" : estUrgente;   // pas de groupe Urgent (Chloé, Joey) : l'urgence de la carte
  const u = (cartesAFaire || []).filter(urgente).length;
  const base = `${n} ${n > 1 ? "choses" : "chose"} à faire`;
  if (u <= 0) return `${base}, aucune urgente.`;
  return `${base}, dont ${u} ${u > 1 ? "urgentes" : "urgente"}.`;
}
